/**
 * Sales Data Tab Component
 * Card-based selection interface for choosing sales data entry method
 * Similar structure to ServiceConsumptionTab
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { SalesDocumentUploadTab } from './SalesDocumentUploadTab';
import { SalesManualEntryTab } from './SalesManualEntryTab';
import { Icon, type IconName } from '../ui/Icon';

// ============================================================================
// Types
// ============================================================================

type ViewState = 'selection' | 'document-scan' | 'manual-entry';

interface SalesOption {
  id: 'document-scan' | 'manual-entry';
  title: string;
  description: string;
  iconName: IconName;
  recommended?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  width: 100%;
  min-width: 0;
  margin: 0;
`;

// Selection View Styles
const SelectionView = styled.div`
  text-align: center;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  max-width: 56rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SelectionCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  text-align: left;
  position: relative;
  min-height: 200px;
  padding: 1.5rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  cursor: pointer;
  &:hover {
    border-color: var(--brand-600);
  }
  &:focus-visible {
    outline: 2px solid var(--interactive-focus);
    outline-offset: 3px;
  }
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const CardIcon = styled.div`
  display: flex;
  color: var(--brand-600);
  svg {
    width: 24px;
    height: 24px;
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const CardDescription = styled.p`
  margin: 0;
  max-width: 42ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
  text-align: left;
`;

// Task View Styles
const TaskView = styled.div`
  position: relative;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--brand-600);
  font-size: var(--font-size-base);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  margin-bottom: var(--space-xl);
  padding: var(--space-sm);
  border-radius: var(--radius-sm);
  transition: all 0.2s ease;

  &:hover {
    background: var(--brand-50);
    color: var(--brand-700);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--brand-200);
  }
`;

const TaskContentWrapper = styled.div`
  /* Override page containers within tasks to prevent double padding */
  > div[class*='PageContainer'] {
    max-width: none;
    margin: 0;
    padding: 0;
  }

  /* Ensure forms and components maintain proper spacing */
  > div > div:first-child {
    margin-top: 0;
  }
`;

// ============================================================================
// Component
// ============================================================================

const salesOptions: SalesOption[] = [
  {
    id: 'document-scan',
    title: 'Subir documento de ventas',
    description:
      'Sube un documento de ventas y revisa los cubiertos extraídos antes de guardarlos.',
    iconName: 'file-upload',
    recommended: true,
  },
  {
    id: 'manual-entry',
    title: 'Entrada manual',
    description:
      'Ingresa los datos campo por campo. Ideal si no tienes un documento de reporte a mano.',
    iconName: 'manual',
  },
];

export const SalesDataTab: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewState>('selection');

  const handleOptionSelect = (optionId: 'document-scan' | 'manual-entry') => {
    setCurrentView(optionId);
  };

  const handleBackToSelection = () => {
    setCurrentView('selection');
  };

  const renderSelectionView = () => (
    <SelectionView>
      <CardsContainer>
        {salesOptions.map((option) => (
          <SelectionCard
            key={option.id}
            onClick={() => handleOptionSelect(option.id)}
            aria-describedby={`${option.id}-description`}
          >
            {option.recommended && (
              <RecommendedBadge>Recomendado</RecommendedBadge>
            )}
            <CardIcon>
              <Icon name={option.iconName} size="xl" aria-hidden />
            </CardIcon>
            <CardTitle>{option.title}</CardTitle>
            <CardDescription id={`${option.id}-description`}>
              {option.description}
            </CardDescription>
          </SelectionCard>
        ))}
      </CardsContainer>
    </SelectionView>
  );

  const renderTaskView = () => {
    let taskComponent = null;

    switch (currentView) {
      case 'document-scan':
        taskComponent = <SalesDocumentUploadTab />;
        break;
      case 'manual-entry':
        taskComponent = <SalesManualEntryTab />;
        break;
      default:
        return null;
    }

    return (
      <TaskView>
        <BackButton onClick={handleBackToSelection}>
          ← Volver a selección de método
        </BackButton>
        <TaskContentWrapper>{taskComponent}</TaskContentWrapper>
      </TaskView>
    );
  };

  return (
    <Container>
      {currentView === 'selection' ? renderSelectionView() : renderTaskView()}
    </Container>
  );
};

SalesDataTab.displayName = 'SalesDataTab';
